// OHIF 3.11 deleted @ohif/ui's Dropdown and IconButton; ui-next's DropdownMenu
// (Radix) and Button are the equivalents.
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@ohif/ui-next';
import { NavBar } from './NavBar';
import { Icon } from '../atoms/Icon';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Wordmark } from './brand/Wordmark';

type MenuOption = {
  title: string;
  icon: string;
  onClick: () => void;
};

type HeaderProps = {
  menuOptions?: MenuOption[];
  children?: ReactNode;
  isSticky?: boolean;
  rightContent?: ReactNode;
  /**
   * Always-visible, never scrolled away. Rendered between the scrollable band
   * and the right-hand action group — the MoreTools button and the
   * characterisation controls.
   */
  pinnedContent?: ReactNode;
};

export function Header({
  children,
  menuOptions,
  isSticky,
  rightContent,
  pinnedContent,
}: HeaderProps) {
  const { t } = useTranslation('Header');

  return (
    <NavBar
      className="gleamer-header justify-between border-b border-dl-border bg-dl-chrome"
      isSticky={isSticky}
    >
      {/* `min-w-0` is what lets the middle band actually shrink: a flex item
          defaults to `min-width: auto`, which pins it to its content width and
          pushes the siblings (notably the options menu) off-screen instead. */}
      <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
        {/* Brand/back and the options menu are `shrink-0`: they stay reachable
            at any width, so the header never loses a control off-screen. */}
        <div className="flex shrink-0 items-center">
          <Link
            title={t('Go to tasks')}
            className="mr-3 inline-flex cursor-pointer items-center"
            to="/my-label-tasks"
          >
            <Icon name="chevron-left" className="w-8 text-dl-muted hover:text-dl-text" />
            <div className="ml-3">
              <h1 className="text-[22px] leading-none text-dl-muted">
                <Wordmark className="[&>span:last-child]:text-dl-accent" />
              </h1>
            </div>
          </Link>
        </div>
        {/* The toolbars absorb the width loss first. Rather than relying on a
            scrollbar nobody notices, the Toolbar itself pushes whatever no
            longer fits into the existing pinned "MoreTools" split button. The
            horizontal scroll stays only as a last-resort safety net for the
            other header content. */}
        {/* `h-11` is load-bearing, not cosmetic: GleamerViewerLayout sizes the
            whole viewer body as `calc(100vh - 52px)`, so the header must stay
            exactly 52px or every viewport below it is misaligned. NavBar sets
            `min-height`, not a fixed height, so an overflowing band that
            reserves a scrollbar track would push the header taller; pinning the
            band to 44px (+ NavBar's 4px/4px padding) keeps the scrollbar inside
            the band instead.
            NOTE: an earlier version of this comment blamed that scrollbar for a
            300px -> 287px shift in the smoke snapshots. That was wrong — the
            shift came from Cypress screenshotting resizing the app frame, which
            is why the snapshot step now reads the canvas backing store. */}
        {/* No `flex-1` here, deliberately. Growing the band to fill the leftover
            space left the tools left-aligned inside it and pushed the pinned
            group to the far right edge, so "more tools" ended up floating in the
            middle of the header, detached from the tools it belongs to. The band
            now sizes to its content (flex-grow 0) and still shrinks — `min-w-0`
            plus the default flex-shrink — so the overflow measurement keeps
            working. The leftover space is taken by the spacer below instead. */}
        <div className="gleamer-header-content gleamer-viewer-toolbar flex h-11 min-w-0 items-center justify-start gap-2 overflow-x-auto [scrollbar-width:thin]">
          {children}
        </div>
        {/* Pinned, and deliberately OUTSIDE the scrollable band above.
            Holds the "more tools" button plus the characterisation controls, so
            they sit immediately after the tool row but can never scroll away —
            the tools button in particular has to stay reachable at exactly the
            moment the row overflows, which is when it would otherwise be the
            first thing clipped. Keeping it out of the band also means appending
            to its section cannot change the band's content width, which the
            overflow measurement reads. */}
        {pinnedContent && (
          <div className="gleamer-viewer-toolbar flex shrink-0 items-center">{pinnedContent}</div>
        )}
        {/* Takes the leftover width so it opens up HERE — between the tools and
            the right-hand actions — instead of inside the band. `basis-0` means
            it contributes nothing to the base size, so it never competes with
            the band when space runs short: the band shrinks, this stays at zero,
            and tools spill into the MoreTools dropdown as before. */}
        <div className="min-w-0 flex-1 basis-0" />
        {/* `shrink-0`, deliberately: this holds Validate — the primary action —
            and the options menu. Making this group shrinkable clipped Validate
            off the end of the header, which must never happen. Header width is
            reclaimed by making the confidences control compact instead (see
            PredictionsConfidence), not by squeezing this group. */}
        <div className="flex shrink-0 items-center justify-end">
          {rightContent}
          {menuOptions?.length > 0 && (
            <div className="shrink-0">
              <DropdownMenu>
                <DropdownMenuTrigger asChild id="options">
                  {/* ui-next's Button `size="icon"` is a fixed 24px square — it
                    squashes this two-icon trigger. Use an auto-width size and
                    give each icon an explicit box so they stay legible and
                    vertically centred in the 52px header. */}
                  <Button
                    variant="ghost"
                    size="lg"
                    title={t('Options')}
                    className="gleamer-viewer-options flex items-center gap-0.5 text-dl-muted hover:text-dl-text"
                  >
                    <Icon
                      name="settings"
                      id="options-settings-icon"
                      className="h-6 w-6"
                    />
                    <Icon
                      name="chevron-down"
                      id="options-chevron-down-icon"
                      className="h-4 w-4"
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {menuOptions.map(option => (
                    <DropdownMenuItem
                      key={option.title}
                      onClick={option.onClick}
                      className="flex items-center gap-2"
                    >
                      {option.icon && (
                        <Icon name={option.icon} className="w-4" />
                      )}
                      {option.title}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>
    </NavBar>
  );
}
